import { useSelectedProfile } from 'hooks/useSelectedProfile';
import { useWindowDimensions } from 'react-native';
import { useAnalytics } from 'services/analytics/useAnalytics';
import {
  useCreateFavoriteMutation,
  useDeleteFavoriteMutation,
  useGetFavoritesQuery,
} from 'services/backend';
import { CmsProgram } from 'types';
import { calcSizeForDevice } from 'utils/index';

import { useProgram } from './context';

import Button from 'components/Button';

type SaveProps = {
  program?: CmsProgram;
  variant?: 'vertical' | 'horizontal';
  flexDirection?: 'row' | 'column';
};

const ProgramSave: React.FC<SaveProps> = ({
  program,
  variant = 'vertical',
  flexDirection = 'row',
}) => {
  const programContext = useProgram();
  const { selectedProfile } = useSelectedProfile();

  const programData = program || programContext;
  const defaultHorizontalButtonContent = 'Adicionar à lista';

  const { width } = useWindowDimensions();
  const horizontalButtonContent = width > 768 ? defaultHorizontalButtonContent : '';

  const analytics = useAnalytics();

  const { data } = useGetFavoritesQuery(selectedProfile?.id ?? '');
  const findInStorage = data?.items.find((item) => item.programId === programData._id);

  const [createFavorite] = useCreateFavoriteMutation();
  const [deleteFavorite] = useDeleteFavoriteMutation();

  const handleToggleFavorite = () => {
    if (findInStorage) {
      analytics.triggerRemoveToFavoritesActionEvent({
        programId: programData._id,
        programTitle: programData.title,
        programAgeRating: programData.rating,
        programAdUnit: programData.ads?.adunit?.val!,
        programCategory: programData.category.map((tag) => tag.name).join(', '),
        programDirector: programData.director,
        programGenre: programData.genre.map((genre) => genre.name).join(', '),
        programYear: programData.year,
      });

      deleteFavorite(findInStorage._id).unwrap();
      return;
    }

    analytics.triggerAddToFavoritesActionEvent({
      programId: programData._id,
      programTitle: programData.title,
      programAgeRating: programData.rating,
      programAdUnit: programData.ads?.adunit?.val!,
      programCategory: programData.category.map((tag) => tag.name).join(', '),
      programDirector: programData.director,
      programGenre: programData.genre.map((genre) => genre.name).join(', '),
      programYear: programData.year,
    });

    createFavorite({
      title: programData.title,
      url: programData.url,
      image: programData.imageVertical?.url!,
      programId: programData._id,
      kids: !!programData.kids,
      rating: programData.rating,
      user: selectedProfile?.userId,
      profile: selectedProfile?.id,
    });
  };

  if (variant === 'horizontal') {
    return (
      <Button
        rounded
        style={{
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection,
          padding: 5,
        }}
        variant="outline"
        borderColor="blue"
        textStyle={{ fontSize: 14, color: 'white' }}
        onPress={handleToggleFavorite}
        iconType={findInStorage ? 'SolidBookmark' : 'Bookmark'}
        iconStyle={{ width: calcSizeForDevice(14, 16), height: calcSizeForDevice(14, 16) }}>
        {horizontalButtonContent}
      </Button>
    );
  }

  return (
    <Button
      useTVFocus
      role="link"
      onPress={handleToggleFavorite}
      variant="ghost"
      size="sm"
      iconType={findInStorage ? 'SolidBookmark' : 'Bookmark'}
      iconStyle={{ width: calcSizeForDevice(24, 30), height: calcSizeForDevice(24, 30) }}
      textStyle={{
        fontSize: calcSizeForDevice(14, 20),
        marginTop: calcSizeForDevice(10),
        marginBottom: calcSizeForDevice(7),
      }}
      style={{
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: calcSizeForDevice(5),
        paddingVertical: calcSizeForDevice(10),
        height: calcSizeForDevice(58, 80),
        marginVertical: calcSizeForDevice(20),
      }}>
      Minha lista
    </Button>
  );
};

export default ProgramSave;
